import { Moon, PanelLeftClose, PanelLeftOpen, Plus, Sun } from 'lucide-react';
import { UserAvatar } from '@/components/user-avatar';
import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { useAppStore } from '@/core/store/app.store';
import { useAuth } from '@/core/providers/use-auth';
import { Theme } from '@/domain/core';
import { MENU_LINKS } from '@/layouts/menu/items';
import { CountrySwitcher } from './menu/components/country-switcher';

import iconDark from '@/assets/icon.dark.svg';
import iconWhite from '@/assets/icon.white.svg';

import type { ReactNode } from 'react';

interface HybridLayoutProps {
  children: ReactNode;
}

/**
 * Application shell.
 *
 * Sidebar structure (top → bottom):
 * 1. Logo (links to /) + collapse toggle button
 * 2. Nav links (scrollable) — labels hidden when collapsed
 * 3. CTA — Añadir Punto99 (icon-only when collapsed)
 * 4. Dark mode toggle (icon-only when collapsed)
 * 5. User area (avatar-only when collapsed)
 */
export function HybridLayout(props: HybridLayoutProps) {
  const { user, logout } = useAuth();
  const theme = useAppStore((state) => state.theme);
  const setTheme = useAppStore((state) => state.setTheme);

  const [is_collapsed, setIsCollapsed] = useState(false);

  const is_dark = theme === Theme.DARK;

  const onToggleTheme = () => {
    setTheme(is_dark ? Theme.LIGHT : Theme.DARK);
  };

  const onToggleSidebar = () => {
    setIsCollapsed((previous) => !previous);
  };

  const sidebar_width = is_collapsed ? 'w-14' : 'w-60';

  return (
    <div className="flex h-screen bg-base-200">
      {/* ── Sidebar ── */}
      <aside
        className={twMerge(
          'flex h-full flex-shrink-0 flex-col border-r border-base-300 bg-base-100 transition-all duration-200',
          sidebar_width,
        )}
      >
        {/* Logo + collapse toggle */}
        <div className="flex h-16 items-center border-b border-base-300 px-2 gap-1 shrink-0">
          <Link
            to="/"
            className="flex flex-1 items-center gap-2 rounded-lg px-2 py-1 hover:bg-base-200 transition-colors min-w-0 text-primary"
            title="Ir al inicio"
          >
            <img
              src={is_dark ? iconWhite : iconDark}
              alt="punto99"
              className="h-6 w-auto shrink-0"
            />
            {!is_collapsed && (
              <span className="text-base-content/40 text-xs font-bold tracking-widest uppercase truncate pt-0.5">
                console
              </span>
            )}
          </Link>
          <button
            onClick={onToggleSidebar}
            className="btn btn-ghost btn-xs btn-square shrink-0 opacity-40 hover:opacity-100"
            title={is_collapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            {is_collapsed ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <PanelLeftClose className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Country context switcher */}
        <CountrySwitcher is_collapsed={is_collapsed} />

        {/* Nav links */}
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-2">
          {MENU_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              end={link.path === '/'}
              title={link.label}
              className={({ isActive }) =>
                twMerge(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                  is_collapsed && 'justify-center px-2',
                  isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-base-content hover:bg-base-200',
                )
              }
            >
              <link.Icon className="h-4 w-4 shrink-0" />
              {!is_collapsed && link.label}
            </NavLink>
          ))}
        </nav>

        {/* CTA — Añadir Punto99 */}
        <div className="px-2 pb-2">
          <Link
            to="/dispatch-places/new"
            title="Añadir Punto99"
            className={twMerge(
              'btn btn-success w-full gap-2 text-white shadow-sm',
              is_collapsed && 'px-0',
            )}
          >
            <Plus className="h-4 w-4 shrink-0" />
            {!is_collapsed && 'Añadir Punto99'}
          </Link>
        </div>

        {/* Dark mode toggle */}
        <div
          className={twMerge(
            'flex items-center border-t border-base-300 px-4 py-3',
            is_collapsed ? 'justify-center px-2' : 'justify-between',
          )}
        >
          {!is_collapsed && (
            <div className="flex items-center gap-2 text-xs text-base-content/50">
              {is_dark ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />}
              {is_dark ? 'Modo oscuro' : 'Modo claro'}
            </div>
          )}
          {is_collapsed ? (
            <button
              onClick={onToggleTheme}
              className="btn btn-ghost btn-xs btn-square"
              title={is_dark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            >
              {is_dark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </button>
          ) : (
            <input
              type="checkbox"
              className="toggle toggle-xs toggle-primary"
              checked={is_dark}
              onChange={onToggleTheme}
              aria-label="Cambiar tema"
            />
          )}
        </div>

        {/* User area */}
        <div className="border-t border-base-300 p-3">
          <div className={twMerge('flex items-center gap-3', is_collapsed && 'justify-center')}>
            <UserAvatar name={user?.name} picture={user?.picture} size={32} />
            {!is_collapsed && (
              <>
                <div className="flex flex-1 flex-col overflow-hidden">
                  <span className="truncate text-sm font-medium">{user?.name}</span>
                  <span className="truncate text-xs text-base-content/50">{user?.email}</span>
                </div>
                <button
                  className="btn btn-ghost btn-xs shrink-0"
                  onClick={logout}
                  title="Cerrar sesión"
                >
                  Salir
                </button>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* ── Main content ── */}
      <main className="flex flex-1 flex-col overflow-hidden">
        {/* Topbar */}
        <header className="flex h-16 items-center justify-between border-b border-base-300 bg-base-100 px-6 shrink-0">
          <div className="flex items-center gap-4">
            {/* Country Selector was here */}
          </div>
          <div className="flex items-center gap-4">
            {/* Future topbar actions */}
          </div>
        </header>

        <div className="h-full overflow-y-auto p-6">{props.children}</div>
      </main>
    </div>
  );
}
